import { Button, Flex, Popconfirm, Tooltip, Typography } from "antd";
import styled from "styled-components";
import {
  DotsSixVerticalIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { CSSProperties, ReactNode } from "react";
import { useTranslate } from "@refinedev/core";

const SLIDES_I18N =
  "components.templates.TemplatesBoard.storyboardSlidesThumbnails";

const SCENE_ROW_ACTION_SELECTOR = "[data-scene-row-action]";

const StyledRow = styled(Flex)<{ $selected: boolean; $interactive: boolean }>`
  width: 100%;
  padding: 4px 8px;
  cursor: ${({ $interactive }) => ($interactive ? "pointer" : "default")};
  background-color: ${({ $selected, theme }) =>
    $selected ? theme.colorPrimaryBg : "transparent"};

  &:hover {
    background-color: ${({ $selected, $interactive, theme }) => {
      if ($selected) return theme.colorPrimaryBg;
      if ($interactive) return theme.colorFillTertiary;
      return "transparent";
    }};
  }
`;

const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: grab;
  touch-action: none;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

// Holds the thumbnail + label so a disabled row can be dimmed without fading
// the drag handle / toggle controls.
const Content = styled.div<{ $dimmed: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.45 : 1)};
  transition: opacity 0.15s ease;
`;

const Label = styled(Typography.Text)`
  flex: 1;
  font-size: 12px;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type SceneRowViewProps = {
  label: string;
  thumbnail: ReactNode;
  selected: boolean;
  onSelect: () => void;
  leading?: ReactNode;
  trailing?: ReactNode;
  dimmed?: boolean;
  /** When false the row can't be selected (no click, pointer cursor or hover). */
  interactive?: boolean;
  rowRef?: (node: HTMLElement | null) => void;
  style?: CSSProperties;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes?: Record<string, any>;
  // dnd-kit drag listeners — when present the whole row acts as the drag handle.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dragListeners?: Record<string, any>;
};

const SceneRowView = ({
  label,
  thumbnail,
  selected,
  onSelect,
  leading,
  trailing,
  dimmed = false,
  interactive = true,
  rowRef,
  style,
  attributes,
  dragListeners,
}: SceneRowViewProps) => (
  <StyledRow
    ref={rowRef}
    style={style}
    align="center"
    gap={6}
    $selected={selected}
    $interactive={interactive}
    onClick={
      interactive
        ? (event) => {
            if ((event.target as HTMLElement).closest(SCENE_ROW_ACTION_SELECTOR)) {
              return;
            }
            onSelect();
          }
        : undefined
    }
    {...attributes}
    {...dragListeners}
  >
    {/* Spacer matches the drag handle width so rows without one stay aligned. */}
    {leading ?? <span style={{ width: 20, flexShrink: 0 }} />}
    <Content $dimmed={dimmed}>
      {thumbnail}
      <Label>{label}</Label>
    </Content>
    {trailing}
  </StyledRow>
);

type SceneRowProps = {
  id: string;
  label: string;
  thumbnail: ReactNode;
  selected: boolean;
  onSelect: () => void;
  onDelete?: () => void;
};

/** Draggable scene row — must be rendered inside a dnd-kit SortableContext. */
export const SortableSceneRow = ({
  id,
  label,
  thumbnail,
  selected,
  onSelect,
  onDelete,
}: SceneRowProps) => {
  const t = useTranslate();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    // The order persists through the react-query cache, which re-renders a tick
    // after drop — disabling the layout animation avoids the item animating back
    // to its original slot before the new order lands.
  } = useSortable({ id, animateLayoutChanges: () => false });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <SceneRowView
      label={label}
      thumbnail={thumbnail}
      selected={selected}
      onSelect={onSelect}
      rowRef={setNodeRef}
      style={style}
      attributes={attributes}
      dragListeners={listeners}
      leading={
        // Visual grip only — the whole row is the drag handle (listeners live on
        // the row), so a drag can start from anywhere except the delete button.
        <DragHandle>
          <DotsSixVerticalIcon size={20} weight="bold" />
        </DragHandle>
      }
      trailing={
        onDelete ? (
          // Row click and drag both ignore descendants with data-scene-row-action
          // (Popconfirm overrides the Button onClick, so stopPropagation on the
          // button alone would not prevent the row from selecting/dragging).
          <span
            style={{ display: "inline-flex" }}
            data-scene-row-action
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Tooltip title={t(`${SLIDES_I18N}.removeScene`)}>
              <Popconfirm
                title={t(`${SLIDES_I18N}.removeSceneConfirm`)}
                okText={t(`${SLIDES_I18N}.removeSceneConfirmYes`)}
                cancelText={t(`${SLIDES_I18N}.removeSceneConfirmNo`)}
                onConfirm={onDelete}
                placement="bottomRight"
              >
                <Button
                  type="text"
                  size="small"
                  aria-label={t(`${SLIDES_I18N}.removeScene`)}
                  icon={<TrashIcon size={18} />}
                />
              </Popconfirm>
            </Tooltip>
          </span>
        ) : undefined
      }
    />
  );
};

/** Non-draggable scene row (intro/closing, single chapter scene). */
export const StaticSceneRow = ({
  label,
  thumbnail,
  selected,
  onSelect,
}: Omit<SceneRowProps, "id" | "onDelete">) => (
  <SceneRowView
    label={label}
    thumbnail={thumbnail}
    selected={selected}
    onSelect={onSelect}
  />
);

type ToggleableSceneRowProps = {
  label: string;
  thumbnail: ReactNode;
  selected: boolean;
  enabled: boolean;
  /** Accessible label/tooltip for the eye toggle (e.g. "Toggle intro"). */
  toggleLabel: string;
  onSelect: () => void;
  onToggle: (enabled: boolean) => void;
};

/**
 * A scene row that can be enabled/disabled (transition, intro, closing).
 * When disabled it stays visible but dimmed. The eye toggle shows a crossed
 * eye while the scene is visible (click to hide) and an open eye while hidden.
 */
export const ToggleableSceneRow = ({
  label,
  thumbnail,
  selected,
  enabled,
  toggleLabel,
  onSelect,
  onToggle,
}: ToggleableSceneRowProps) => (
  <SceneRowView
    label={label}
    thumbnail={thumbnail}
    selected={selected}
    onSelect={onSelect}
    dimmed={!enabled}
    interactive={enabled}
    trailing={
      <Tooltip title={toggleLabel}>
        <Button
          type="text"
          size="small"
          aria-label={toggleLabel}
          icon={enabled ? <EyeSlashIcon size={16} /> : <EyeIcon size={16} />}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(!enabled);
          }}
        />
      </Tooltip>
    }
  />
);
